import { HrefLanguageModel, PageMetaData } from '@/types/B2BcontentServerContract';
import { KeyValuePair } from '@/types/B2CcontentServerContract';

class DomService {
    private static VIEWPORT = 'viewport';

    public updateMetaData(metadata: PageMetaData) {
        if (metadata) {
            this.setPageTitle(metadata.title);
            this.setCanonicalLink(metadata.canonical);
            metadata.metaTags && this.setMetaNameTags(metadata.metaTags);
            this.setHrefLanguages(metadata.hrefLanguages);
            metadata.openGraphMetaTags && this.setMetaPropertyTags(metadata.openGraphMetaTags);
        }
    }

    public setPageTitle(title: string): void {
        if (!title) return;
        document.title = title;
    }

    private setMetaNameTags(tags: KeyValuePair<string, string>[]): void {
        tags.forEach(tag => this.setContent(tag.key, null, tag.value));
    }

    private setMetaPropertyTags(tags: KeyValuePair<string, string>[]): void {
        if (!tags) return;
        tags.forEach(tag => this.setContent(null, tag.key, tag.value));
    }

    private setCanonicalLink(href: string): void {
        if (!href) return;
        let link: HTMLLinkElement | null = this.getCanonicalLink();
        if (href) {
            link.setAttribute('href', href);
        } else {
            const headElement = this.getHeadElement();
            headElement && headElement.removeChild(link);
            link = null;
        }
    }

    setHrefLanguages(hrefLanguages: HrefLanguageModel[]) {
        if (!hrefLanguages) return;
        const headElement = this.getHeadElement();
        if (headElement) {
            const existinghrefLinks = headElement.querySelectorAll('link[rel="alternate"]') as NodeListOf<HTMLElement>;
            existinghrefLinks.forEach(link => {
                headElement.removeChild(link);
            });

            if (hrefLanguages) {
                hrefLanguages.forEach(hrefLanguage => {
                    const link = document.createElement('link');
                    link.setAttribute('rel', 'alternate');
                    link.setAttribute('hreflang', hrefLanguage.culture);
                    link.setAttribute('href', hrefLanguage.url);
                    headElement.appendChild(link);
                });
            }
        }
    }

    public getMetaViewport() {
        return this.getMetaElement(DomService.VIEWPORT).content;
    }

    public setMetaViewport(content: string) {
        return this.setContent(DomService.VIEWPORT, null, content);
    }

    private getHeadElement(): HTMLHeadElement | null {
        return document.head;
    }

    private setContent(nameAttribute: string | null = null, propertyAttribute: string | null = null, content: string | null | undefined): HTMLMetaElement {
        const tag = this.getMetaElement(nameAttribute, propertyAttribute);
        tag.content = this.sanitizeInput(content);
        return tag;
    }

    private sanitizeInput(input: string | null | undefined): string {
        return input || '';
    }

    private getCanonicalLink(): HTMLLinkElement {
        return this.getOrCreate<HTMLLinkElement>('link[rel="canonical"]', () => {
            const link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            const headElement = this.getHeadElement();
            if (headElement) {
                headElement.appendChild(link);
            }
            return link;
        });
    }

    private getMetaElement(nameAttribute: string | null = null, propertyAttribute: string | null = null): HTMLMetaElement {
        return this.getOrCreate<HTMLMetaElement>('meta[' + (nameAttribute ? 'name="' + nameAttribute + '"' : '') + (propertyAttribute ? 'property="' + propertyAttribute + '"' : '') + ']', () => {
            const element = document.createElement('meta');
            if (nameAttribute) {
                element.name = nameAttribute;
            }
            if (propertyAttribute) {
                element.setAttribute('property', propertyAttribute);
            }
            const headElement = this.getHeadElement();
            if (headElement) {
                headElement.appendChild(element);
            }
            return element;
        });
    }

    private getOrCreate<TElement extends HTMLElement>(selector: string, create?: () => TElement): TElement {
        const element = document.querySelector(selector) as TElement;
        if (element) {
            return element;
        }
        if (create) {
            return create();
        }
        throw new Error(`Provide create param when element ${selector} cannot be found.`);
    }
}

export default new DomService();
